/**
 * Freehand pen tool.
 *
 * On pointer-down we create a fresh `freedraw` element at the pointer
 * position, start collecting point+pressure samples, and let the canvas'
 * live-layer preview render the in-progress stroke. On pointer-up we
 * finalise the element: compute the bbox, rebase points to be relative
 * to (x, y), and commit via `store.upsertLocal` (which pushes undo).
 *
 * During the drag we DO NOT commit to the store on every point — that
 * would produce hundreds of undo entries per scribble and flood the wire
 * in Stage E. The preview is a pure function of our local sample buffer.
 *
 * Stage H: while the pointer is down we also fire throttled
 * `scene/stroke-live` broadcasts so other connections see the stroke
 * appear in real time. The draft element id is generated up front so
 * every batch refers to the same id; the committed `scene/op` on release
 * uses that same id, letting receivers seamlessly swap the transient
 * preview for the persisted element.
 */

import { nanoid } from "nanoid";
import type { SceneElement } from "../../lib/scene";
import type { SceneStore } from "../store/sceneStore";
import { drawElement } from "../render/drawElement";
import { currentSceneClient } from "../net/currentClient";
import type { Tool, ToolPointerEvent } from "./tool";

/** Minimum gap between outgoing stroke-live batches, ms. ~20 Hz. */
const STROKE_LIVE_INTERVAL_MS = 50;

export class PenTool implements Tool {
  readonly kind = "pen";

  private samples: Array<{ x: number; y: number; p: number }> = [];
  private active = false;
  /** Stable id for the in-progress stroke — reused by the commit op. */
  private draftId: string | null = null;
  private lastBroadcastAt = 0;

  isActive(): boolean {
    return this.active;
  }

  onPointerDown(evt: ToolPointerEvent, store: SceneStore): void {
    if (evt.button !== 0) return; // left click only
    this.active = true;
    this.samples = [{ x: evt.x, y: evt.y, p: evt.pressure }];
    this.draftId = nanoid(14);
    this.lastBroadcastAt = 0;
    // Prime tool options for broadcast.
    void store;
  }

  onPointerMove(evt: ToolPointerEvent, store: SceneStore): void {
    if (!this.active) return;
    // Drop near-duplicate samples so tiny jitter doesn't bloat the array.
    const last = this.samples[this.samples.length - 1];
    const dx = evt.x - last.x;
    const dy = evt.y - last.y;
    if (dx * dx + dy * dy < 0.25) return; // < 0.5 map-unit move — skip
    this.samples.push({ x: evt.x, y: evt.y, p: evt.pressure });

    // Throttled live broadcast. We send the full sample buffer each
    // batch (not a delta) so late joiners or dropped packets can't
    // desync the preview — the receiving client just overwrites.
    const now = performance.now();
    if (now - this.lastBroadcastAt < STROKE_LIVE_INTERVAL_MS) return;
    this.lastBroadcastAt = now;
    const client = currentSceneClient.instance;
    if (!client || !this.draftId || this.samples.length < 2) return;
    const points: number[] = [];
    const pressures: number[] = [];
    for (const s of this.samples) {
      points.push(s.x, s.y);
      pressures.push(s.p);
    }
    const opts = store.getState().toolOptions;
    client.sendStrokeLive(this.draftId, points, pressures, opts.strokeColor, opts.strokeWidth);
  }

  onPointerUp(_evt: ToolPointerEvent, store: SceneStore): void {
    if (!this.active) return;
    this.active = false;

    if (this.samples.length < 2) {
      this.samples = [];
      this.draftId = null;
      return;
    }

    // Rebase relative to the bbox min so (x, y) is the origin and
    // points/pressures are purely local. This matches how rect/line/arrow
    // store their geometry and keeps hit-testing consistent.
    let minX = this.samples[0].x,
      minY = this.samples[0].y,
      maxX = minX,
      maxY = minY;
    for (const s of this.samples) {
      if (s.x < minX) minX = s.x;
      if (s.x > maxX) maxX = s.x;
      if (s.y < minY) minY = s.y;
      if (s.y > maxY) maxY = s.y;
    }
    const points: number[] = [];
    const pressures: number[] = [];
    for (const s of this.samples) {
      points.push(s.x - minX, s.y - minY);
      pressures.push(s.p);
    }

    const el: SceneElement = store.createElement("freedraw", {
      // Reuse the draft id so the committed scene/op overwrites the
      // transient live-stroke preview on every remote client.
      ...(this.draftId ? { id: this.draftId } : {}),
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
      points,
      pressures,
    });
    store.upsertLocal(el);
    this.samples = [];
    this.draftId = null;
  }

  cancel(): void {
    this.active = false;
    this.samples = [];
    this.draftId = null;
  }

  drawPreview(ctx: CanvasRenderingContext2D, store: SceneStore): void {
    if (!this.active || this.samples.length < 2) return;
    // Reuse the same renderer by building a throwaway element the same
    // shape as the eventual committed element. Means the preview looks
    // exactly like the result — no surprise style jump on release.
    let minX = this.samples[0].x,
      minY = this.samples[0].y,
      maxX = minX,
      maxY = minY;
    for (const s of this.samples) {
      if (s.x < minX) minX = s.x;
      if (s.x > maxX) maxX = s.x;
      if (s.y < minY) minY = s.y;
      if (s.y > maxY) maxY = s.y;
    }
    const points: number[] = [];
    const pressures: number[] = [];
    for (const s of this.samples) {
      points.push(s.x - minX, s.y - minY);
      pressures.push(s.p);
    }
    const preview = store.createElement("freedraw", {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
      points,
      pressures,
    });
    drawElement(ctx, preview);
  }
}
